<template>
    <div>
        <div class="pt-2 max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <h1 class="text-2xl font-semibold text-gray-900">
                <SmartBreadcrumbs
                    :pages="[
                        {
                            name: $t('messages.settings'),
                            href: null,
                        },
                    ]"
                />
            </h1>
        </div>

        <div class="max-w-7xl mx-auto md:px-4 sm:px-6 md:px-8">
            <FormSection>
                <template v-slot:title>
                    {{ $t('messages.avatar') }}
                </template>
                <template v-slot:content>
                    <div class="md:grid md:grid-cols-3 md:gap-6">
                        <div>
                            <img
                                :src="user.avatar_or_profile_photo"
                                alt="avatar"
                                class="mx-auto rounded-full h-32 w-32 border-4 border-white"
                            />
                        </div>

                        <div v-if="can.upload_avatar">
                            <div>
                                {{ $tc('messages.upload_photo', 2) }}
                            </div>
                            <AvatarUploader :endpoint="links.avatar_store" />
                        </div>
                    </div>
                </template>
            </FormSection>
        </div>

        <PanelPage>
            <form method="POST" :action="route('students.settings.update')" @submit.prevent="submit">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ $tc('messages.languages', 1) }}
                        </h3>
                        <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="md:grid md:grid-cols-6 md:gap-6">
                            <div class="sm:col-span-3">
                                <label
                                    for="preferred_language"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                >
                                    {{ $tc('messages.languages', 1) }}
                                </label>
                                <div class="mt-1 rounded-md shadow-sm">
                                    <select
                                        id="preferred_language"
                                        v-model="form.preferred_language"
                                        name="preferred_language"
                                        class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    >
                                        <option value="" disabled>
                                            {{ $t('messages.select') }}
                                        </option>
                                        <option v-for="language in languages" :value="language" :key="language">
                                            {{ $t(`locales.${language}`) }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ $t('messages.change_password') }}
                        </h3>
                        <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="md:grid md:grid-cols-6 md:gap-6">
                            <div class="sm:col-span-3">
                                <div class="mt-1">
                                    <label for="password" class="block text-sm font-medium leading-5 text-gray-700">
                                        {{ $t('messages.password') }}
                                    </label>
                                    <div class="mt-1 rounded-md shadow-sm">
                                        <input
                                            id="password"
                                            name="password"
                                            autocomplete="new-password"
                                            v-model="form.password"
                                            type="password"
                                            minlength="8"
                                            class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        />
                                    </div>
                                    <p
                                        class="mt-2 text-sm text-red-600"
                                        id="email-error"
                                        v-if="$page.props.errors.password"
                                    >
                                        {{ $page.props.errors.password }}
                                    </p>
                                </div>
                            </div>
                            <div class="sm:col-span-3">
                                <div class="mt-1">
                                    <label
                                        for="password_confirmation"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                    >
                                        {{ $t('messages.password_repeat') }}
                                    </label>
                                    <div class="mt-1 rounded-md shadow-sm">
                                        <input
                                            id="password_confirmation"
                                            name="password_confirmation"
                                            autocomplete="new-password"
                                            v-model="form.password_confirmation"
                                            type="password"
                                            minlength="8"
                                            class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="flex justify-end mt-5">
                        <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
                    </div>
                </div>
            </form>
        </PanelPage>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AvatarUploader from '@/components/pageSpecific/AvatarUploader.vue';
import FormSection from '@/components/FormSection.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        AvatarUploader,
        FormSection,
    },
    layout: Layout,
    name: 'Edit',
    props: ['languages', 'user', 'can', 'links'],
    data() {
        return {
            form: {
                preferred_language: this.user.preferred_language,
                password: null,
                password_confirmation: null,
            },
        };
    },
    methods: {
        submit() {
            this.$inertia.patch(this.route('students.settings.update'), this.form, {
                onFinish: () => {
                    this.$i18n.locale = this.user.preferred_language;
                },
            });
        },
    },
};
</script>
